
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import axios, { AxiosError } from "axios";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import store from "@/store";
import JwtService from "./core/services/JwtService";
import { Actions, AssetsMutations } from "@/store/enums/StoreEnums";

export default defineComponent({
  name: "app",

  setup() {
    const router = useRouter();
    const isIdle = ref(false);

    const checkForUpdates = () => {
      const currentBuildTime = document.documentElement.getAttribute(
        "data-build-timestamp-utc"
      );
      if (currentBuildTime) {
        fetch(window.location.href, {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        })
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const newBuildTime = doc.documentElement.getAttribute(
              "data-build-timestamp-utc"
            );
            if (newBuildTime && newBuildTime !== currentBuildTime) {
              console.log("Nueva versión detectada, recargando...");
              window.location.reload();
            }
          })
          .catch(() => {
            // For other types of errors
          });
      }
    };

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    });

    if (process.env.NODE_ENV === "production") {
      setInterval(checkForUpdates, 10 * 60 * 1000);
    }

    store.subscribe((mutation) => {
      if (
        mutation.type == "idleVue/IDLE_CHANGED" &&
        store.getters.isUserAuthenticated
      ) {
        isIdle.value = mutation.payload;
        if (mutation.payload == false) {
          store.dispatch(Actions.VERIFY_AUTH).catch(() => {
            router.push("/login");
          });
        }
      }

      if (mutation.type == AssetsMutations.LOADING) {
        if (mutation.payload) {
          Swal.fire({
            title: "Cargando",
            text: "Espere un momento por favor",
            icon: "info",
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        } else {
          Swal.close();
        }
      }
    });

    const authFreePaths = [
      "auth",
      "auth/password/reset",
      "auth/password/reset/confirm",
    ];

    axios.interceptors.request.use(
      (config) => {
        store.commit(AssetsMutations.SET_PENDING_REQUEST);
        const token = JwtService.getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        const path = config.url?.split("?")[0];
        if (path && !authFreePaths.includes(path) && !token) {
          return Promise.reject("Unauthorized");
        }
        return config;
      },
      (error) => {
        store.commit(AssetsMutations.CLEAR_PENDING_REQUEST);
        return Promise.reject(error);
      }
    );

    axios.defaults.timeout = process.env.VUE_APP_AXIOS_TIMEOUT;

    axios.interceptors.response.use(
      (response) => {
        store.commit(AssetsMutations.CLEAR_PENDING_REQUEST);
        return response;
      },
      (error: AxiosError) => {
        store.commit(AssetsMutations.CLEAR_PENDING_REQUEST);

        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          Swal.fire({
            title: "Timeout",
            text: "El servidor está tardando en responder.",
            icon: "error",
            confirmButtonText: "Aceptar",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
          return { store, isIdle };
        }

        if (error.response?.status === 401) {
          const token = JwtService.getToken();
          const requestUrl = error.config?.url || "";
          const path = requestUrl.split("?")[0];
          const isAuthPath = path && authFreePaths.includes(path);
          const showExpiredMessage = token && !isAuthPath;

          store.dispatch(Actions.LOGOUT);
          router.push("/login");

          if (showExpiredMessage) {
            Swal.fire({
              title: "Sesión expirada",
              text: "Su sesión ha expirado o no tiene una sesión válida con el servidor y fue redireccionado a la página de ingreso.",
              icon: "error",
              confirmButtonText: "Aceptar",
              customClass: {
                confirmButton: "btn btn-primary",
              },
            });
          }

          return { store, isIdle };
        }

        if (!error.response) {
          Swal.fire({
            title: "Server Error",
            text: "No hay respuesta del servidor",
            icon: "error",
            confirmButtonText: "Aceptar",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
          return { store, isIdle };
        }

        return Promise.reject(error);
      }
    );

    return { store, isIdle };
  },
});
